import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { color } from '@/colors';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';
import { useGlobalStore } from '@/state/global.state';
import { unpublishWorkflowFromCommunity } from '@/api/workflowCommunityPublishing.api';
import { WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { invalidateWorkflowCommunityMetadata } from './useWorkflowCommunityMetadata';
import {
  getCommunityPublishErrorPayload,
  type WorkflowCommunityPublishTrackingContext,
} from './workflowCommunityPublishAnalytics';

const logger = log.getLogger('useUnpublishWorkflowFromCommunity');

const UNPUBLISH = I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.UNPUBLISH;

export const useUnpublishWorkflowFromCommunity = (
  weaveWorkflowId: string,
  trackingContext?: WorkflowCommunityPublishTrackingContext,
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const { track } = useAnalytics();

  const trackingPayload = useMemo(
    () =>
      trackingContext
        ? {
            ...trackingContext,
            workflowId: trackingContext.workflowId ?? weaveWorkflowId,
          }
        : undefined,
    [trackingContext, weaveWorkflowId],
  );

  return useCallback(async () => {
    try {
      await unpublishWorkflowFromCommunity(weaveWorkflowId);
    } catch (error) {
      if (trackingPayload) {
        track(
          WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.UNPUBLISH_RESULT,
          {
            ...trackingPayload,
            result: 'failure',
            ...getCommunityPublishErrorPayload(error),
          },
          TrackTypeEnum.Product,
        );
      }
      logger.error('Failed to unpublish workflow from community', error, {
        weaveWorkflowId,
        status: isAxiosError(error) ? error.response?.status : undefined,
      });
      updateSnackbarData({ text: t(UNPUBLISH.FAILURE_SNACKBAR), isOpen: true });
      return;
    }

    try {
      await invalidateWorkflowCommunityMetadata(queryClient, weaveWorkflowId);
    } catch (refetchError) {
      logger.error('Failed to refetch metadata after unpublish', refetchError, {
        weaveWorkflowId,
        status: isAxiosError(refetchError) ? refetchError.response?.status : undefined,
      });
    }

    if (trackingPayload) {
      track(
        WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.UNPUBLISH_RESULT,
        {
          ...trackingPayload,
          result: 'success',
        },
        TrackTypeEnum.Product,
      );
    }

    updateSnackbarData({
      text: t(UNPUBLISH.SUCCESS_SNACKBAR),
      icon: <CheckMarkCircleIcon width={16} height={16} style={{ color: color.Black100 }} />,
      isOpen: true,
    });
  }, [weaveWorkflowId, queryClient, updateSnackbarData, t, track, trackingPayload]);
};
